/**
 * AK-584 — состояние настройки ТЕНАНТА для мастера, баннера, чек-листа и
 * пустого состояния списка полей.
 *
 * Источник — `/api/v1/scope/available`, а НЕ `/dashboard/summary`: сводка
 * дашборда считается по активному скоупу сессии, и «полей 0» там означает «в
 * выбранном хозяйстве нет полей». Клиенту с 800 полями, вставшему скоупом на
 * пустое хозяйство, мы бы на каждой странице писали «Настройка не закончена»
 * (ревью head-web по PR #746, блокер 1). Строки `/scope/available` от текущего
 * выбора не зависят.
 *
 * Общий query-ключ на всех потребителей — сводка тянется один раз.
 * Ошибку НЕ проглатываем: баннер при ошибке молчит, а страница
 * «Продолжить настройку» показывает честное «не удалось загрузить» вместо
 * выдуманных нулей.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchScopeAvailable } from '@/lib/http/sharedQueries';
import {
  tenantCountsFromScopes,
  type ScopeRow,
  type TenantCounts,
} from './setupState';

interface AvailableScopesResponse {
  available?: ScopeRow[];
}

export const setupSummaryKey = ['onboarding', 'tenant-counts'] as const;

export interface SetupStatus {
  /** Счётчики по тенанту; `null` — состав неизвестен (пустой ответ). */
  counts: TenantCounts | null;
  isLoading: boolean;
  isError: boolean;
}

export function useSetupStatus(): SetupStatus {
  const query = useQuery({
    queryKey: setupSummaryKey,
    queryFn: async () => {
      // Перф-аудит 14.08 (корень 6): та же ручка, что у селектора скоупа и
      // карты, — идём через общий кэш, чтобы это был ОДИН сетевой запрос на
      // всех потребителей, а не третий параллельный.
      const res = await fetchScopeAvailable<AvailableScopesResponse>();
      return tenantCountsFromScopes(res.available);
    },
    // Настройка меняется редко; минуты свежести хватает, чтобы баннер ушёл
    // на следующем переходе после сохранения первого поля.
    staleTime: 60_000,
    retry: false,
  });

  return {
    counts: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/** Сбросить счётчики после действия, которое меняет состояние настройки. */
export function useInvalidateSetupStatus() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: setupSummaryKey });
  };
}
